import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { colors, spacing, typography, borderRadius } from '../styles/designTokens'

function RegisterPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
    setError('')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Wachtwoorden komen niet overeen')
      return
    }

    if (formData.password.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten')
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Registratie mislukt')
      }

      alert('Registratie succesvol! U kunt nu inloggen.')
      navigate('/login')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registratie mislukt')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: `2px solid ${colors.gray300}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    boxSizing: 'border-box' as const
  }

  const labelStyle = {
    display: 'block',
    marginBottom: spacing.sm,
    fontSize: '14px',
    fontWeight: 500,
    color: colors.textPrimary
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden'
    }}>
      {/* Left Side - Register Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '48px',
        background: colors.white,
        maxWidth: '600px',
        overflowY: 'auto'
      }}>
        {/* Logo */}
        <div style={{
          marginBottom: '40px',
          width: 'fit-content'
        }}>
          <img
            src="/cropped-cropped-LOGO1-2.png"
            alt="LDH TRANS"
            style={{
              height: '60px',
              width: 'auto'
            }}
          />
        </div>

        {/* Form */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '400px'
        }}>
          <h1 style={{
            ...typography.h1,
            margin: 0,
            marginBottom: spacing.xl,
            color: colors.textPrimary
          }}>
            Registreren
          </h1>

          {error && (
            <div style={{
              background: '#FEE2E2',
              color: '#DC2626',
              padding: '12px 16px',
              borderRadius: borderRadius.sm,
              marginBottom: spacing.lg,
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.lg
          }}>
            {/* Name Fields */}
            <div style={{ display: 'flex', gap: spacing.md }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Voornaam</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Achternaam</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  required
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                style={inputStyle}
              />
            </div>

            {/* Phone */}
            <div>
              <label style={labelStyle}>Telefoonnummer</label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange('phoneNumber')}
                placeholder="+32 xxx xx xx xx"
                style={inputStyle}
              />
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>Wachtwoord</label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  style={{ ...inputStyle, paddingRight: '48px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    color: colors.gray500
                  }}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label style={labelStyle}>Bevestig wachtwoord</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                required
                style={inputStyle}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                background: colors.primary,
                color: colors.white,
                border: 'none',
                borderRadius: '24px',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                marginTop: spacing.md,
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? 'Registreren...' : 'Registreren'}
            </button>
          </form>

          {/* Login Link */}
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              marginTop: spacing.xl,
              padding: 0
            }}
          >
            Heeft u al een account? Log hier in.
          </button>
        </div>
      </div>

      {/* Right Side - Watermark */}
      <div style={{
        flex: 1,
        background: 'linear-gradient(135deg, #FFE5E8 0%, #FFF0F1 50%, #FFFFFF 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px',
          opacity: 0.15,
          transform: 'rotate(-3deg) scale(1.5)',
          userSelect: 'none'
        }}>
          <img
            src="/cropped-cropped-LOGO1-2.png"
            alt="LDH TRANS"
            style={{
              width: '500px',
              height: 'auto'
            }}
          />
          <span style={{
            fontSize: '80px',
            fontWeight: 900,
            color: colors.primary,
            letterSpacing: '12px'
          }}>
            LDH TRANS
          </span>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
